/**
 * Correspondance des lots tirés d'un dépôt (ARTO-130, spec 2026-10-11 § 8.2 à
 * 8.4) : le lot qu'on crée depuis un dépôt, son profil de sources, les règles
 * de correspondance et leur aperçu, la transformation en morceaux de pivot, et
 * la bibliothèque de profils. `docs/dev/reprise.md` § Correspondance et
 * `docs/dev/api.md` § 12.16 donnent le contrat.
 *
 * Les types ci-dessous reprennent les formes de l'API ; les écrans ne lisent
 * qu'eux. En mode `?demo`, aucune requête réseau (`demoCorrespondance.ts`).
 */
import { ApiError, estErreurConfinement, modeDemo, requete } from './api.js';
import { signalerConfinement } from './confinement.js';
import { demoCorrespondance } from './demoCorrespondance.js';
import type { LotReprise } from './reprise.js';

export const NATURES_PIVOT = [
  'lieux',
  'artistes',
  'oeuvres',
  'contacts',
  'expositions',
  'localisations',
  'provenance',
  'editions',
  'membres',
] as const;
export type NaturePivot = (typeof NATURES_PIVOT)[number];

export function estNaturePivot(valeur: string): valeur is NaturePivot {
  return (NATURES_PIVOT as readonly string[]).includes(valeur);
}

/**
 * Les types que la correspondance sait lire (`console/correspondance.ts`) :
 * tableurs, JSON, XML, vCard. Un autre fichier du dépôt se voit, il ne se cite pas.
 */
export const TYPES_SOURCE_LISIBLES: readonly string[] = [
  'text/csv',
  'text/tab-separated-values',
  'text/plain',
  'application/json',
  'application/x-ndjson',
  'application/xml',
  'text/vcard',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

// ── Règles ───────────────────────────────────────────────────────────────

/** Un champ source : un en-tête ou un chemin, dans une source ou dans l'élément parent. */
export interface RefSource {
  champ: string;
  role: string | null;
  parent: boolean;
}

export const FORMATS_DATE = ['JJ/MM/AAAA', 'MM/JJ/AAAA', 'AAAA', 'excel_serie'] as const;
export type FormatDate = (typeof FORMATS_DATE)[number];

export const DEVISES_MONTANT = ['EUR', 'USD', 'GBP', 'CHF'] as const;
export type DeviseMontant = (typeof DEVISES_MONTANT)[number];

/** La liste fermée des transformations : aucune expression, aucun code. */
export const TYPES_TRANSFORMATION = [
  'majuscules',
  'minuscules',
  'titre',
  'scinder',
  'extraire_nombre',
  'date',
  'montant',
  'dictionnaire',
  'constante',
  'si_vide',
  'concat',
  'prefixe',
] as const;
export type TypeTransformation = (typeof TYPES_TRANSFORMATION)[number];

export interface Dictionnaire {
  valeurs: Record<string, string>;
  defaut?: string;
}

export type Transformation =
  | { type: 'majuscules' | 'minuscules' | 'titre' | 'extraire_nombre' }
  | { type: 'scinder'; separateur: string; index: number }
  | { type: 'date'; format: FormatDate }
  | { type: 'montant'; decimal: ',' | '.'; devise?: DeviseMontant }
  | { type: 'dictionnaire'; valeurs: Record<string, string>; defaut?: string }
  | { type: 'constante'; valeur: string }
  | { type: 'si_vide'; valeur?: string; source?: RefSource }
  | { type: 'concat'; source: RefSource; separateur: string }
  | { type: 'prefixe'; valeur: string };

/** Un champ du pivot : ses sources, jointes par `separateur`, puis ses transformations. */
export interface ChampRegle {
  sources: RefSource[];
  separateur: string;
  transformations: Transformation[];
}

export type OperateurFiltre = 'egal' | 'different' | 'vide' | 'non_vide';
export const OPERATEURS_FILTRE: readonly OperateurFiltre[] = [
  'egal',
  'different',
  'vide',
  'non_vide',
];

/** Une condition qu'une ligne doit remplir pour être GARDÉE. */
export interface FiltreRegle {
  source: RefSource;
  operateur: OperateurFiltre;
  valeur: string;
}

export interface BlocNature {
  colonnes: Record<string, ChampRegle>;
  cleOrigine: RefSource | null;
  filtres: FiltreRegle[];
  dedoublonner: string | null;
}

export interface SourceRegle {
  role: string;
  feuille: string | null;
  racine: string | null;
}

export interface JointureRegle {
  role: string;
  cle: string;
  sur: string;
  mode: 'un_a_un' | 'un_a_plusieurs';
}

export interface Regles {
  sources: SourceRegle[];
  jointures: JointureRegle[];
  deplier: { chemin: string } | null;
  filtres: FiltreRegle[];
  natures: Record<string, BlocNature>;
}

/** Les choix d'une galerie, posés sur son lot : jamais dans un profil partagé. */
export interface Surcharges {
  fichiers: Record<string, string>;
  dictionnaires: Record<string, Dictionnaire>;
  constantes: Record<string, string>;
}

export const SURCHARGES_VIDES: Surcharges = { fichiers: {}, dictionnaires: {}, constantes: {} };

/** Une erreur de règle : `chemin` désigne le champ (`natures.oeuvres.colonnes.titre`). */
export interface ErreurRegle {
  chemin: string;
  message: string;
}

// ── Champs du pivot ──────────────────────────────────────────────────────

export interface ColonnePivot {
  cle: string;
  entete: string;
  aide: string;
  obligatoire: boolean;
}

export interface CiblePivot {
  nature: NaturePivot;
  colonnes: ColonnePivot[];
}

export interface CiblesPivot {
  natures: CiblePivot[];
}

// ── Profil des sources (`profil.json`) ───────────────────────────────────

export type FormatColonne =
  | 'vide'
  | 'date_iso'
  | 'date_fr'
  | 'date_us'
  | 'date_ambigue'
  | 'annee'
  | 'excel_serie'
  | 'courriel'
  | 'url'
  | 'devise'
  | 'montant'
  | 'nombre'
  | 'liste'
  | 'texte';

export interface ColonneProfilee {
  nom: string;
  /** Part des lignes où la colonne est remplie, de 0 à 1. */
  remplissage: number;
  distinctes: number;
  frequentes: { valeur: string; nombre: number }[];
  format: FormatColonne;
  conformite: number;
  separateur?: string;
  decimal?: ',' | '.';
}

export interface TableProfilee {
  /** La feuille, ou le chemin du tableau ; `null` : la table unique du fichier. */
  nom: string | null;
  lignes: number;
  entetes: string[];
  colonnes: ColonneProfilee[];
  echantillon: { ligne: number; valeurs: Record<string, unknown> }[];
}

export interface SuggestionProfil {
  profilId: string;
  version: number;
  nom: string;
  application: string;
  nature: string;
  /** De 0 à 100. */
  score: number;
  trouves: number;
  attendus: number;
}

export interface FichierProfile {
  fichierId: string | null;
  chemin: string;
  octets: number;
  sha256?: string;
  format?: string;
  encodage?: string | null;
  separateur?: string | null;
  tables?: TableProfilee[];
  chemins?: { chemin: string; tableau: boolean; occurrences: number }[];
  suggestions?: SuggestionProfil[];
  /** Une source illisible : la raison, et rien d'autre. */
  erreur?: { raison: string; message: string };
}

export interface ProfilSources {
  version: number;
  lotId: string;
  etabliLe: string;
  fichiers: FichierProfile[];
}

// ── Aperçu et transformation ─────────────────────────────────────────────

export interface ErreurSource {
  fichier: string;
  ligne: number;
  element?: number;
  nature: string | null;
  colonne: string | null;
  message: string;
}

export interface SortieApercu {
  colonnes: { cle: string; entete: string }[];
  lignes: Record<string, string>[];
  filtres: number;
  doublons: number;
  vides: number;
  refusees: number;
}

export interface ApercuCorrespondance {
  echantillon: true;
  lus: number;
  lignes: number;
  filtres: number;
  natures: Record<string, SortieApercu>;
  erreurs: ErreurSource[];
  avertissements: ErreurSource[];
}

export interface BilanNature {
  produites: number;
  filtres: number;
  doublons: number;
  vides: number;
  refusees: number;
}

export interface RapportTransformation {
  lus: number;
  lignes: number;
  filtres: number;
  natures: Record<string, BilanNature>;
  morceaux: { nature: string; rang: number; lignes: number; octets: number }[];
  erreurs: ErreurSource[];
  erreursTotal: number;
  avertissements: ErreurSource[];
  avertissementsTotal: number;
}

export interface GroupeMessage {
  message: string;
  nombre: number;
  occurrences: {
    morceau: { nature: string; rang: number } | null;
    lotId: string;
    ligne: number;
    libelle: string;
  }[];
}

/** L'essai d'un lot transformé : le total de ses morceaux, refus groupés par message. */
export interface RapportEssaiMorceaux {
  lotId: string;
  morceaux: number;
  essayes: number;
  total: number;
  crees: number;
  completes: number;
  ignores: number;
  refuses: number;
  erreurs: GroupeMessage[];
  avertissements: GroupeMessage[];
}

// ── Profils de la bibliothèque ───────────────────────────────────────────

export interface ProfilResume {
  id: string;
  version: number;
  nom: string;
  application: string;
  nature: NaturePivot;
  natures: string[];
  signature: string[];
  fourni: boolean;
  auteur: string | null;
  creeLe: string;
}

export interface ProfilComplet extends ProfilResume {
  regles: Regles;
}

export interface NouveauProfil {
  nom: string;
  application: string;
  nature: NaturePivot;
  signature: string[];
  regles: Regles;
}

export interface VersionProfil {
  nom: string;
  application: string;
  signature: string[];
  regles: Regles;
  /** La version qu'on a sous les yeux, celle que la nouvelle remplace. */
  version: number;
}

// ── Appels ───────────────────────────────────────────────────────────────

const base = (idGalerie: string): string =>
  `/v1/console/galleries/${encodeURIComponent(idGalerie)}/reprise`;
const cheminLot = (idGalerie: string, lotId: string): string =>
  `${base(idGalerie)}/lots/${encodeURIComponent(lotId)}`;

export interface CreationLotDepot {
  depotId: string;
  /** 1 à 10 fichiers acceptés du dépôt, par leur identifiant. */
  fichiers: string[];
  nature: NaturePivot;
}

export async function creerLotDepuisDepot(
  idGalerie: string,
  corps: CreationLotDepot,
): Promise<LotReprise> {
  if (modeDemo()) return demoCorrespondance.creerLot(idGalerie, corps);
  return requete<LotReprise>(`${base(idGalerie)}/lots`, { methode: 'POST', corps });
}

export async function profilerLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) return demoCorrespondance.profiler(idGalerie, lotId);
  return requete<LotReprise>(`${cheminLot(idGalerie, lotId)}/profiler`, { methode: 'POST' });
}

export async function transformerLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) return demoCorrespondance.transformer(idGalerie, lotId);
  return requete<LotReprise>(`${cheminLot(idGalerie, lotId)}/transformer`, { methode: 'POST' });
}

/** Cite une version exacte d'un profil, et pose (ou retire) les surcharges de la galerie. */
export interface ChoixCorrespondance {
  revision: number;
  profil?: { id: string; version: number } | null;
  surcharges?: Surcharges | null;
}

export async function choisirCorrespondance(
  idGalerie: string,
  lotId: string,
  corps: ChoixCorrespondance,
): Promise<LotReprise> {
  if (modeDemo()) return demoCorrespondance.choisir(idGalerie, lotId, corps);
  return requete<LotReprise>(cheminLot(idGalerie, lotId), { methode: 'PATCH', corps });
}

export async function lireProfilSources(idGalerie: string, lotId: string): Promise<ProfilSources> {
  if (modeDemo()) return demoCorrespondance.profilSources(idGalerie, lotId);
  return requete<ProfilSources>(`${cheminLot(idGalerie, lotId)}/profil`);
}

/** Les surcharges rangées du lot, ou `null` quand il n'en a pas. */
export async function lireSurcharges(idGalerie: string, lotId: string): Promise<Surcharges | null> {
  if (modeDemo()) return demoCorrespondance.surcharges(idGalerie, lotId);
  try {
    const brutes = await requete<Partial<Surcharges>>(`${cheminLot(idGalerie, lotId)}/surcharges`);
    return {
      fichiers: brutes.fichiers ?? {},
      dictionnaires: brutes.dictionnaires ?? {},
      constantes: brutes.constantes ?? {},
    };
  } catch (erreur) {
    if (erreur instanceof ApiError && erreur.status === 404) return null;
    throw erreur;
  }
}

export async function lireTransformation(
  idGalerie: string,
  lotId: string,
): Promise<RapportTransformation> {
  if (modeDemo()) return demoCorrespondance.transformation(idGalerie, lotId);
  return requete<RapportTransformation>(`${cheminLot(idGalerie, lotId)}/transformation`);
}

export async function apercuCorrespondance(
  idGalerie: string,
  lotId: string,
  corps: { regles: Regles; surcharges: Surcharges },
): Promise<ApercuCorrespondance> {
  if (modeDemo()) return demoCorrespondance.apercu(idGalerie, lotId, corps);
  return requete<ApercuCorrespondance>(`${cheminLot(idGalerie, lotId)}/apercu`, {
    methode: 'POST',
    corps,
  });
}

export async function lireRapportEssaiMorceaux(
  idGalerie: string,
  lotId: string,
): Promise<RapportEssaiMorceaux> {
  if (modeDemo()) return demoCorrespondance.rapportEssai(idGalerie, lotId);
  return requete<RapportEssaiMorceaux>(`${cheminLot(idGalerie, lotId)}/rapport?type=essai`);
}

/** Télécharge le pivot en CSV : un morceau, ou tous ceux d'une nature sous un seul en-tête. */
export async function telechargerPivot(
  idGalerie: string,
  lotId: string,
  cible: { nature: NaturePivot; morceau?: number },
): Promise<void> {
  const nomFichier =
    cible.morceau === undefined
      ? `pivot-${cible.nature}.csv`
      : `pivot-${cible.nature}-${String(cible.morceau)}.csv`;
  let blob: Blob;
  if (modeDemo()) {
    blob = new Blob([demoCorrespondance.pivot(idGalerie, lotId, cible)], {
      type: 'text/csv;charset=utf-8',
    });
  } else {
    const parametres = new URLSearchParams({ nature: cible.nature });
    if (cible.morceau !== undefined) parametres.set('morceau', String(cible.morceau));
    const reponse = await fetch(`${cheminLot(idGalerie, lotId)}/pivot?${parametres.toString()}`, {
      credentials: 'include',
    });
    if (!reponse.ok) {
      const erreur = (await reponse.json().catch(() => null)) as {
        code?: string;
        message?: string;
        details?: unknown;
      } | null;
      const refus = new ApiError(
        reponse.status,
        erreur?.code ?? 'erreur_inconnue',
        erreur?.message ?? 'Une erreur est survenue.',
        erreur?.details ?? null,
      );
      if (estErreurConfinement(refus)) signalerConfinement();
      throw refus;
    }
    blob = await reponse.blob();
  }
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.append(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}

// ── Appels : bibliothèque de profils (`/v1/console/profils`) ─────────────

const PROFILS = '/v1/console/profils';

export async function lireCibles(): Promise<CiblesPivot> {
  if (modeDemo()) return demoCorrespondance.cibles();
  return requete<CiblesPivot>(`${PROFILS}/cibles`);
}

export async function listerProfils(nature?: NaturePivot): Promise<ProfilResume[]> {
  if (modeDemo()) return demoCorrespondance.profils(nature);
  const parametre = nature ? `?nature=${nature}` : '';
  return (await requete<{ profils: ProfilResume[] }>(`${PROFILS}${parametre}`)).profils;
}

export async function lireProfil(id: string, version?: number): Promise<ProfilComplet> {
  if (modeDemo()) return demoCorrespondance.profil(id, version);
  const parametre = version === undefined ? '' : `?version=${String(version)}`;
  return requete<ProfilComplet>(`${PROFILS}/${encodeURIComponent(id)}${parametre}`);
}

export async function listerVersionsProfil(id: string): Promise<ProfilResume[]> {
  if (modeDemo()) return demoCorrespondance.versions(id);
  return (
    await requete<{ versions: ProfilResume[] }>(`${PROFILS}/${encodeURIComponent(id)}/versions`)
  ).versions;
}

export async function creerProfil(corps: NouveauProfil): Promise<ProfilComplet> {
  if (modeDemo()) return demoCorrespondance.creerProfil(corps);
  return requete<ProfilComplet>(PROFILS, { methode: 'POST', corps });
}

/** La version suivante du profil ; `409 version_perimee` si une autre a été publiée entre-temps. */
export async function publierVersionProfil(
  id: string,
  corps: VersionProfil,
): Promise<ProfilComplet> {
  if (modeDemo()) return demoCorrespondance.publierVersion(id, corps);
  return requete<ProfilComplet>(`${PROFILS}/${encodeURIComponent(id)}`, {
    methode: 'PUT',
    corps,
  });
}

// ── Erreurs nommées ──────────────────────────────────────────────────────

/** Les raisons de l'API que l'écran de correspondance dit en clair. */
export const RAISONS_CORRESPONDANCE = [
  'depot_inutilisable',
  'fichier_inutilisable',
  'fichier_en_double',
  'format_non_pris_en_charge',
  'sources_trop_lourdes',
  'lot_sans_depot',
  'travail_en_cours',
  'profil_requis',
  'profil_hors_nature',
  'morceaux_lances',
  'lot_non_profile',
  'lot_non_transforme',
  'lot_de_correspondance',
  'rien_a_essayer',
  'role_sans_fichier',
  'table_sans_echantillon',
  'version_perimee',
  'regles_invalides',
  'ambigus_a_trancher',
  'lot_sans_images',
] as const;
export type RaisonCorrespondance = (typeof RAISONS_CORRESPONDANCE)[number];

export function raisonCorrespondance(erreur: unknown): RaisonCorrespondance | null {
  if (!(erreur instanceof ApiError)) return null;
  const details = erreur.details as { raison?: unknown } | null;
  for (const candidate of [details?.raison, erreur.code]) {
    const trouvee = RAISONS_CORRESPONDANCE.find((raison) => raison === candidate);
    if (trouvee) return trouvee;
  }
  return null;
}

/** Les erreurs de règles d'un 400 `regles_invalides` : où, et pourquoi. */
export function erreursDeRegles(erreur: unknown): ErreurRegle[] {
  if (!(erreur instanceof ApiError) || erreur.status !== 400) return [];
  const champs = (erreur.details as { champs?: unknown } | null)?.champs;
  if (!Array.isArray(champs)) return [];
  return champs.flatMap((champ: unknown) => {
    const { chemin, message } = (champ ?? {}) as { chemin?: unknown; message?: unknown };
    return typeof chemin === 'string' && typeof message === 'string' ? [{ chemin, message }] : [];
  });
}
